import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  TextField,
  MenuItem,
  Autocomplete,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Badge,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import { useParams, useNavigate } from "react-router-dom";
import JobLayoutContainer from "../../layouts/JobLayoutContainer";
import { useAuth } from "../../context/AuthContext";

import MaintenanceTable from "../../components/jobs/MaintenanceTable";
import IssueTable from "../../components/jobs/IssueTable";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000";

// Must match RegisterWorkPage.jsx's job_type dropdown exactly.
const JOB_TYPES = ["Installation", "Repair", "Maintenance", "Inspection", "Others"];

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role?.toLowerCase() || "engineer";
  const isAdmin = role === "admin";

  const [job, setJob] = useState(null);
  const [maintenance, setMaintenance] = useState([]);    // ✅ keep maintenance for PDF
  const [issues, setIssues] = useState([]);              // ✅ keep issues for PDF
  const [statusLog, setStatusLog] = useState([]);        // Feature 3: activity timeline
  const [assignments, setAssignments] = useState([]);    // Feature 2: real assigned users
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  // dialogs
  const [openClose, setOpenClose] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  // Feature 4: Edit Job
  const [openEdit, setOpenEdit] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [selectedAssignees, setSelectedAssignees] = useState([]); // Feature 2
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  const [fetchError, setFetchError] = useState(null); // { status, message } | null

  const fetchJob = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}`);
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        setFetchError({
          status: res.status,
          message: data.error || `Server responded with ${res.status}`,
        });
        setJob(null);
        return;
      }

      setJob(data.job || null);
      setMaintenance(Array.isArray(data.maintenance) ? data.maintenance : []);
      setIssues(Array.isArray(data.issues) ? data.issues : []);
      setStatusLog(Array.isArray(data.status_log) ? data.status_log : []);
      setAssignments(Array.isArray(data.assignments) ? data.assignments : []);
    } catch (e) {
      console.error(e);
      setFetchError({ status: 0, message: e.message || "Network error" });
      setJob(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    fetch(`${API_BASE}/api/jobs/issues/assignable-users`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setAssignableUsers(data?.users || []))
      .catch((e) => console.error("Failed to load assignable users:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <JobLayoutContainer title="Loading…" role={role} />;
  }
  if (!job) {
    // A real 404 ("this job genuinely doesn't exist") looks different
    // from every other failure (500, network error, etc.) — collapsing
    // them into the same "Job Not Found" message hides real bugs behind
    // a misleading label, which is exactly what happened here before.
    const is404 = fetchError?.status === 404;
    return (
      <JobLayoutContainer title={is404 ? "Job Not Found" : "Couldn't Load This Job"} role={role}>
        <Box sx={{ p: 3 }}>
          <Typography color={is404 ? "text.secondary" : "error"}>
            {is404
              ? "No job matches this link."
              : `Something went wrong: ${fetchError?.message || "Unknown error"}`}
          </Typography>
          <Button sx={{ mt: 2 }} variant="outlined" onClick={fetchJob}>
            Try Again
          </Button>
        </Box>
      </JobLayoutContainer>
    );
  }

  const isCompleted = (job.status || "").toLowerCase() === "completed";

  const StatusChip = ({ value }) => {
    const v = (value || "").toLowerCase();
    const bg = v === "completed" ? "#2e7d32" : "#1e88e5";
    return (
      <Chip
        label={(v || "—").toUpperCase()}
        size="small"
        sx={{ bgcolor: bg, color: "#fff", fontWeight: 700 }}
      />
    );
  };

  // ----- Actions -----
  const handleCloseJob = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: "completed",
          changed_by: user?.user_id || null,
          changed_by_name: user?.name || user?.displayName || "Unknown user",
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to close job");
      setOpenClose(false);
      fetchJob();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteJob = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}`, { method: "DELETE" });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to delete job");
      setOpenDelete(false);
      navigate("/jobs/manage");
    } catch (e) {
      console.error(e);
    }
  };

  // Feature 4: Edit Job — previously nothing about a job could be
  // corrected after registration, only closed or deleted entirely.
  const openEditDialog = () => {
    setEditForm({
      title: job.title || "",
      job_type: job.job_type || "",
      location: job.location || "",
      client_name: job.client_name || "",
      job_condition: job.job_condition || "",
      materials_text: job.materials_text || "",
      team_text: job.team_list || job.team_text || "",
    });
    setSelectedAssignees(assignments);
    setEditError("");
    setOpenEdit(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((f) => ({ ...f, [name]: value }));
  };

  const toggleAssignee = (user) => {
    setSelectedAssignees((prev) =>
      prev.some((u) => u.id === user.id)
        ? prev.filter((u) => u.id !== user.id)
        : [...prev, user]
    );
  };

  const handleSaveEdit = async () => {
    setEditSaving(true);
    setEditError("");
    try {
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to update job");

      // Real assignment link (feature 2) — separate endpoint from the
      // free-text team_text field above, which stays as a display note.
      const assignRes = await fetch(`${API_BASE}/api/jobs/${jobId}/assignments`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ user_ids: selectedAssignees.map((u) => u.id) }),
      });
      const assignData = await assignRes.json();
      if (!assignData.success) throw new Error(assignData.error || "Failed to update assignments");

      setOpenEdit(false);
      fetchJob();
    } catch (e) {
      setEditError(e.message || "Failed to update job");
    } finally {
      setEditSaving(false);
    }
  };

 // ===== PDF EXPORT (Overview → Issues → Maintenance) =====
const exportFullReport = async () => {
  try {
    const { default: jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;

    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const marginX = 40;
    let cursorY = 50;

    // ----- Placeholder Header -----
    doc.setFontSize(18);
    doc.text("SFV TECH", marginX, cursorY);
    cursorY += 20;
    doc.setFontSize(14);
    doc.text("Job Report", marginX, cursorY);
    cursorY += 10;
    doc.setDrawColor(0);
    doc.line(marginX, cursorY, 555, cursorY);
    cursorY += 25;

    // ----- Overview Block -----
    const teamLine = assignments.length
      ? assignments.map((a) => a.name).join(", ")
      : job.team_list || job.team_text || "—";

    const overview = [
      ["Job Reference", job.job_ref || "—"],
      ["Title", job.title || "—"],
      ["Client", job.client_name || "—"],
      ["Type", job.job_type || job.type || "—"],
      ["Status", (job.status || "—").toUpperCase()],
      ["Location", job.location || "—"],
      ["Job Condition", job.job_condition || "—"],
      ["Materials Used", job.materials_text || "—"],
      ["Team", teamLine],
      ["Registered By", job.created_by_name || "—"],
      ["Start Date (UTC)", job.start_date_utc || "—"],
      ["Last Update (UTC)", job.updated_at_utc || job.updated_at || "—"],
    ];

    doc.setFontSize(12);
    const valueMaxWidth = 555 - (marginX + 160); // page width minus label column
    overview.forEach(([k, v]) => {
      doc.setFont(undefined, "bold");
      doc.text(`${k}:`, marginX, cursorY);
      doc.setFont(undefined, "normal");

      const lines = doc.splitTextToSize(String(v), valueMaxWidth);
      doc.text(lines, marginX + 160, cursorY);
      cursorY += 18 * lines.length;
    });

    cursorY += 15;
    const newPageIfNeeded = () => {
      if (cursorY > 750) {
        doc.addPage();
        cursorY = 40;
      }
    };

    // ===== ISSUES SECTION =====
    newPageIfNeeded();
    doc.setFontSize(13);
    doc.setFont(undefined, "bold");
    doc.text("Issues", marginX, cursorY);
    cursorY += 12;
    doc.setFont(undefined, "normal");

    if (!issues?.length) {
      cursorY += 16;
      doc.text("No issues recorded.", marginX, cursorY);
      cursorY += 20;
    } else {
      autoTable(doc, {
        startY: cursorY + 4,
        head: [["Opened At (UTC)", "Title", "Description", "Status", "Resolution Note"]],
        body: issues.map((r) => [
          r.opened_at_utc || "—",
          r.title || "—",
          r.description || "—",
          (r.status || "—").toUpperCase(),
          r.resolution_note || "—",
        ]),
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [33, 150, 243] }, // blue
        margin: { left: marginX, right: marginX },
      });
      cursorY = doc.lastAutoTable.finalY + 25;
    }

    // ===== MAINTENANCE SECTION =====
    newPageIfNeeded();
    doc.setFontSize(13);
    doc.setFont(undefined, "bold");
    doc.text("Maintenance", marginX, cursorY);
    cursorY += 12;
    doc.setFont(undefined, "normal");

    if (!maintenance?.length) {
      cursorY += 16;
      doc.text("No maintenance records.", marginX, cursorY);
    } else {
      autoTable(doc, {
        startY: cursorY + 4,
        head: [["Date / Time (UTC)", "Performed By", "Team", "Description", "Materials Used", "Next Date (UTC)"]],
        body: maintenance.map((m) => [
          m.date_time || m.date_time_utc || "—",
          m.performed_by || "—",
          m.team || m.team_name || "—",
          m.description || "—",
          m.materials_used || m.materials || "—",
          m.next_date || m.next_date_utc || "—",
        ]),
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [76, 175, 80] }, // green
        margin: { left: marginX, right: marginX },
      });
    }

    // ===== FILE NAME FORMAT =====
    const fileName = `job_REPORT_${(job.job_ref || "JOB").replace(/\s+/g, "_")}.pdf`;
    doc.save(fileName);
  } catch (err) {
    alert("Failed to generate the report. Please try again — if this keeps happening, check the console for details.");
    console.error("Export report error:", err);
  }
};


  return (
    <JobLayoutContainer title={`${job.job_ref} — Details`} role={role}>
      {/* ===== Overview Card ===== */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Overview
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <DetailItem label="Job Reference" value={job.job_ref} />
            <DetailItem label="Title" value={job.title} />
            <DetailItem label="Client" value={job.client_name || "—"} />
          </Grid>
          <Grid item xs={12} md={6}>
            <DetailItem label="Status" value={<StatusChip value={job.status} />} />
            <DetailItem label="Registered By" value={job.created_by_name || "—"} />
            <DetailItem label="Start Date" value={job.start_date_utc || "—"} />
            <DetailItem label="Last Update" value={job.updated_at_utc || "—"} />
          </Grid>
        </Grid>

        {/* ===== Feature 3: horizontal compact info bar =====
            Location, Job Type, Job Condition, Materials Used, and Team —
            as a single wrapping row instead of stacking each vertically,
            to conserve space. All editable via "Edit Job" below, but only
            while the job is still ongoing. */}
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            p: 1.5,
            bgcolor: "action.hover",
            borderRadius: 2,
          }}
        >
          <Chip label={`Type: ${job.job_type || "—"}`} size="small" />
          <Chip label={`Location: ${job.location || "—"}`} size="small" />
          <Chip label={`Condition: ${job.job_condition || "—"}`} size="small" />
          <Chip label={`Materials: ${job.materials_text || "—"}`} size="small" />
          <Chip
            label={
              assignments.length
                ? `Team: ${assignments.map((a) => a.name).join(", ")}`
                : job.team_list
                ? `Team (unlinked): ${job.team_list}`
                : "Team: Unassigned"
            }
            size="small"
            color={assignments.length ? "primary" : "default"}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ===== Action Buttons ===== */}
        {/* Add Maintenance / Add Issue removed from here — they used to
            navigate to /add-maintenance and /add-issue, pages that never
            existed. MaintenanceTable.jsx and IssueTable.jsx below already
            have their own working "Add" buttons that open an in-page
            dialog instead of navigating anywhere. */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Tooltip title={isCompleted ? "Reopen the job (Close Job status) before editing its details" : ""}>
            <span>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={openEditDialog}
                disabled={isCompleted}
              >
                Edit Job
              </Button>
            </span>
          </Tooltip>

          {/* Close Job is visible ONLY when status is NOT completed */}
          {!isCompleted && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => setOpenClose(true)}
            >
              Close Job
            </Button>
          )}

          {/* Delete is visible for Admin ALWAYS (even if completed) */}
          {isAdmin && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setOpenDelete(true)}
            >
              Delete Job
            </Button>
          )}

          {/* ===== NEW: Export Full Report ===== */}
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportFullReport}
            sx={{ ml: "auto" }}
          >
            Export Full Report
          </Button>
        </Box>
      </Paper>

      {/* ===== Maintenance Section ===== */}
      <Paper sx={{ p: 0, mb: 2, bgcolor: "transparent", boxShadow: "none" }}>
        <MaintenanceTable jobId={jobId} />
      </Paper>

      {/* ===== Issues Section ===== */}
      <Paper sx={{ p: 0, bgcolor: "transparent", boxShadow: "none" }}>
        <IssueTable jobId={jobId} />
      </Paper>

      {/* ===== Activity Timeline (Feature 3) ===== */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HistoryIcon fontSize="small" /> Activity Timeline
        </Typography>
        {statusLog.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No status changes recorded yet.
          </Typography>
        ) : (
          <Box>
            {statusLog.map((entry, idx) => {
              const key = entry.id ?? entry.log_id ?? entry.status_log_id ?? idx;
              const rawWhen =
                entry.changed_at ?? entry.changed_at_utc ?? entry.created_at ?? entry.timestamp ?? entry.logged_at;
              const whenLabel = rawWhen ? new Date(rawWhen).toLocaleString() : "—";
              // Statuses are stored lowercase ("ongoing", "completed") —
              // show them properly capitalized here instead of raw.
              const formatStatus = (s) =>
                s ? String(s).charAt(0).toUpperCase() + String(s).slice(1).toLowerCase() : "—";

              return (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    gap: 2,
                    py: 1.25,
                    borderBottom: idx === statusLog.length - 1 ? "none" : "1px solid #f1f1f1",
                  }}
                >
                  <Typography variant="caption" sx={{ minWidth: 150, opacity: 0.6 }}>
                    {whenLabel}
                  </Typography>
                  <Box>
                    <Typography variant="body2">
                      {entry.old_status ? (
                        <>
                          Status changed from <b>{formatStatus(entry.old_status)}</b> to{" "}
                          <b>{formatStatus(entry.new_status)}</b>
                        </>
                      ) : (
                        <>Job created, status set to <b>{formatStatus(entry.new_status)}</b></>
                      )}
                    </Typography>
                    {entry.note && (
                      <Typography variant="caption" color="text.secondary">
                        {entry.note}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* ===== EDIT JOB DIALOG ===== */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Job</DialogTitle>
        <DialogContent dividers>
          {editForm && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField label="Job Title" name="title" fullWidth value={editForm.title} onChange={handleEditChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Job Type" name="job_type" fullWidth value={editForm.job_type} onChange={handleEditChange}>
                  {JOB_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Location" name="location" fullWidth value={editForm.location} onChange={handleEditChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Client Name" name="client_name" fullWidth value={editForm.client_name} onChange={handleEditChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Job Condition / Notes" name="job_condition" fullWidth multiline minRows={2} value={editForm.job_condition} onChange={handleEditChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Materials Used" name="materials_text" fullWidth multiline minRows={2} value={editForm.materials_text} onChange={handleEditChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Team Members" name="team_text" fullWidth multiline minRows={2} value={editForm.team_text} onChange={handleEditChange} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                  Assigned Users (real accounts)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  This is the real link used for each person's "My Assigned Jobs" — separate from the
                  free-text Team Members note above. The number badge shows how many other jobs
                  someone is already assigned to.
                </Typography>

                <TextField
                  size="small"
                  fullWidth
                  placeholder="Filter the list..."
                  value={assigneeSearch}
                  onChange={(e) => setAssigneeSearch(e.target.value)}
                  sx={{ mb: 1 }}
                />

                <Box
                  sx={{
                    maxHeight: 260,
                    overflowY: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {assignableUsers
                    .filter((u) =>
                      !assigneeSearch.trim()
                        ? true
                        : `${u.name} ${u.role}`.toLowerCase().includes(assigneeSearch.trim().toLowerCase())
                    )
                    .map((u) => {
                      const checked = selectedAssignees.some((sel) => sel.id === u.id);
                      return (
                        <Box
                          key={u.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            pr: 1,
                          }}
                        >
                          <FormControlLabel
                            control={<Checkbox checked={checked} onChange={() => toggleAssignee(u)} />}
                            label={`${u.name} (${u.role})`}
                          />
                          {u.job_count > 0 && (
                            <Tooltip title={`Already assigned to ${u.job_count} job(s)`}>
                              <Badge badgeContent={u.job_count} color="primary" sx={{ mr: 1.5 }}>
                                <Chip size="small" label="jobs" variant="outlined" />
                              </Badge>
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })}
                  {assignableUsers.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      No users found.
                    </Typography>
                  )}
                </Box>
              </Grid>
              {editError && (
                <Grid item xs={12}>
                  <Typography color="error">{editError}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)} disabled={editSaving}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={editSaving}>
            {editSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== CLOSE JOB DIALOG ===== */}
      <Dialog open={openClose} onClose={() => setOpenClose(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark as Completed?</DialogTitle>
        <DialogContent dividers>
          <Typography>
            You are about to close <b>{job.job_ref}</b>. This will mark the job as
            <b> completed</b>. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenClose(false)}>Cancel</Button>
          <Button onClick={handleCloseJob} variant="contained" startIcon={<CheckCircleIcon />}>
            Close Job
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== DELETE JOB DIALOG ===== */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: "error.main" }}>Delete Job?</DialogTitle>
        <DialogContent dividers>
          <Typography>
            This will permanently delete <b>{job.job_ref}</b> and all associated records
            (maintenance, issues, etc). This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
          <Button onClick={handleDeleteJob} color="error" variant="contained" startIcon={<DeleteIcon />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </JobLayoutContainer>
  );
}

function DetailItem({ label, value }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ opacity: 0.6 }}>
        {label}
      </Typography>
      <Typography fontWeight={600} component="div">{value}</Typography>
    </Box>
  );
}
